import React from 'react'

import { css, Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import {
  BankLinkedIcon,
  BitcoinLightningIcon,
  CheckIcon,
  DiscountTagIcon,
  FavoriteIcon,
  FlashOnIcon,
  HyperlinkIcon,
  iconIdMapping,
  OverdraftProtectionIcon,
  PayInFourIcon,
  RecurringAutomaticIcon,
  RoundUpsIcon,
} from '@arcade/web/icons/react'

import { pxToRem } from '../../theme'
import arcadeColors from '../../theme/arcadeColors'
import { useArcadeMigrationFeatureFlag } from '../ArcadeComponentMigration/ArcadeComponentMigrationProvider'
import { Flex } from '../layout/Flex'
import { CashDesignSystemContext } from '../Provider'
import Text from '../Text/Text'
import { Avatar as ArcadeAvatar, AvatarProps as ArcadeAvatarProps, DEFAULT_CARD_SCALE_FACTOR } from './ArcadeAvatar'
import { arcadeCustomerColor, arcadeCustomerColorWithTheme } from './ArcadeCustomerColor'

export type AvatarIconType = 'Monogram' | 'Logo' | 'UserImage' | 'Icon'
export type Size = 'xsmall' | 'small' | 'medium' | 'large'
export type CustomerData = {
  threadedCustomerToken?: string
  customerToken?: string
  fullName?: string
  smsNumber?: string
  emailAddress?: string
}

export const profilePicDiameter: {
  [key in Size]: { rem: string; width: number; fontSize: string; cardHeight: string }
} = {
  xsmall: { rem: '2rem', width: 32, fontSize: '1rem', cardHeight: '1.125rem' },
  small: { rem: '3rem', width: 48, fontSize: '1.5rem', cardHeight: '1.95rem' },
  medium: { rem: '4rem', width: 64, fontSize: '2rem', cardHeight: '2rem' },
  large: { rem: '6rem', width: 96, fontSize: '3rem', cardHeight: '2.4rem' },
}

// Stacked Avatar does not support xsmall size, the stacked circl are rendered one size smaller since we are jamming two of them together
const StackedSizeMap: { [key in Exclude<Size, 'xsmall'>]: Size } = {
  small: 'xsmall',
  medium: 'small',
  large: 'medium',
}

type AvatarWrapperProps = {
  size: Size
  background?: string
  borderColor?: string
  disabled?: boolean
  isStacked?: boolean
  color?: string
}

const badgeIconMapping = (icon: string) => {
  switch (icon) {
    case 'RECURRING_OVERLAY':
      return <RecurringAutomaticIcon />
    case 'LOYALTY_OVERLAY':
      return <FavoriteIcon />
    case 'LINKED_ACCOUNT':
      return <BankLinkedIcon />
    case 'LIGHTNING_BOLT':
      return <BitcoinLightningIcon />
    case 'ROUND_UP':
      return <RoundUpsIcon />
    case 'INSTANT_PAY_LIGHTNING_BOLT':
      return <FlashOnIcon />
    case 'COUPON_OVERLAY':
      return <DiscountTagIcon />
    case 'OVERDRAFT_PROTECTION':
      return <OverdraftProtectionIcon />
    case 'PAYMENT_PLAN_OVERLAY':
      return <PayInFourIcon />
    // following icons defined in https://prototype.sqprod.co/#/docs/squareup.franklin.api.FormBlocker.Element.AvatarElement.Badge
    case 'LINK':
      return <HyperlinkIcon />
    case 'PAYMENT_PLAN':
      return <PayInFourIcon />
    case 'CHECKMARK':
      return <CheckIcon />
    default:
      return null
  }
}

const AvatarWrapper = styled(Flex)<AvatarWrapperProps>`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.border.radius.circle};
  border: ${({ borderColor }) => (borderColor ? `${pxToRem(1)} solid ${borderColor};` : undefined)};
  font-weight: ${({ theme }) => theme.weights.medium}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  ${({ size, background }) => {
    return css`
      height: ${profilePicDiameter[size].rem};
      width: ${profilePicDiameter[size].rem};
      font-size: ${profilePicDiameter[size].fontSize}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
      background-color: ${background ?? 'none'};
    `
  }}
  ${({ disabled }) => disabled && 'opacity: 40%;'};
  ${({ color }) => color && `color: ${color};`}

  user-select: none;
`

export type AvatarData = {
  image_url?: string
  dark_theme_image_url?: string
  accent_color?: string
  dark_theme_accent_color?: string
  border_color?: string
  dark_theme_border_color?: string
  icon?: { arcade_id?: string }
  initial?: string
  colorize_image?: boolean
}

const getImageUrlByTheme = (theme: Theme, avatarData?: AvatarData) => {
  if (!avatarData) return
  return theme.darkMode && avatarData.dark_theme_image_url ? avatarData.dark_theme_image_url : avatarData.image_url
}

type UrlImageProps = {
  colorizeImage?: boolean
}
const UrlImage = styled.img<UrlImageProps>`
  width: 100%;
  ${({ colorizeImage }) => {
    if (colorizeImage) {
      return css`
        filter: brightness(0) invert(1);
      `
    }
    return ''
  }}
`

const UrlImageAvatar = ({
  avatarData,
  size = 'medium',
  theme,
  alt,
}: {
  avatarData: AvatarData
  size: Size
  theme: Theme
  alt: string
}) => {
  const imageUrl = getImageUrlByTheme(theme, avatarData)
  const { imageUrlHandler } = React.useContext(CashDesignSystemContext)
  if (!imageUrlHandler) {
    throw new Error('imageUrlHandler is not provided but required in Avatar component')
  }
  const width =
    profilePicDiameter[size].width <= profilePicDiameter.medium.width
      ? profilePicDiameter.medium.width
      : profilePicDiameter[size].width
  if (imageUrl) {
    const img = imageUrlHandler({ url: imageUrl, width })

    return <UrlImage alt={alt} colorizeImage={avatarData.colorize_image} src={img} />
  }

  return null
}

const MonogramAvatar = ({ avatarData }: { avatarData: AvatarData }) => {
  return <Text color="textInverse">{avatarData.initial}</Text>
}

const getThemedAvatarColor = ({
  theme,
  avatarData,
  customerData,
}: {
  theme: Theme
  avatarData: AvatarData
  customerData?: CustomerData
}) => {
  const arcadeBackgroundColor = arcadeCustomerColor({
    threadedCustomerToken: customerData?.threadedCustomerToken,
    customerToken: customerData?.customerToken,
    fullName: customerData?.fullName,
    smsNumber: customerData?.smsNumber,
    emailAddress: customerData?.emailAddress,
  })

  const backgroundColor = avatarData.accent_color || arcadeBackgroundColor

  return (theme.darkMode ? avatarData.dark_theme_accent_color : backgroundColor) ?? backgroundColor
}

const defaultIconStyling = (size: Size) => css`
  height: ${profilePicDiameter[size].rem};
  width: ${profilePicDiameter[size].rem};
`

const MultiAvatarWrapper = styled.div<{ size: Size; disabled?: boolean }>`
  ${props => defaultIconStyling(props.size)}
  border-radius: ${({ theme }) => theme.border.radius.large};
  position: relative;
  ${props => props.disabled && 'opacity: 40%;'}
`

type TopIconProps = {
  shape: 'CIRCULAR_BADGE_SHAPE' | 'CARD_SHAPE' | 'CIRCULAR_SHAPE'
  size: Size
}

const StockIcon = styled(Flex)<TopIconProps>`
  position: absolute;
  box-sizing: content-box;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.border.radius.circle};
  border: ${({ theme }) => `0.125rem solid ${theme.colors.cardBackground};`};
  bottom: -0.125rem;
  right: -0.125rem;

  ${props => defaultIconStyling(props.size)}
  ${props =>
    props.shape === 'CARD_SHAPE' &&
    css`
      border-radius: 0.4rem;
      width: ${profilePicDiameter[props.size].rem};
      height: ${profilePicDiameter[props.size].cardHeight};
    `}
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StackedAvatar = ({
  avatarData,
  avatarType,
  overlay,
  overlayShape,
  size = 'medium',
  alt = 'avatar image',
  disabled = false,
}: {
  avatarData: AvatarData
  avatarType: AvatarIconType
  overlay: string
  overlayShape: 'CIRCULAR_BADGE_SHAPE' | 'CARD_SHAPE' | 'CIRCULAR_SHAPE'
  size: Size
  alt?: string
  disabled?: boolean
}) => {
  const { imageUrlHandler } = React.useContext(CashDesignSystemContext)
  if (!imageUrlHandler) {
    throw new Error('imageUrlHandler is not provided but required in Avatar component')
  }
  if (size === 'xsmall') {
    throw new Error('Stacked Avatar does not support xsmall size')
  }
  const stackedIconSize = StackedSizeMap[size]
  const overlayUrl = imageUrlHandler({ url: overlay, width: profilePicDiameter[stackedIconSize].width })
  return (
    <MultiAvatarWrapper disabled={disabled} size={size}>
      <BaseAvatar avatarData={avatarData} avatarType={avatarType} size={stackedIconSize} alt={alt} />
      <StockIcon size={stackedIconSize} shape={overlayShape}>
        <img src={overlayUrl} alt={alt} />
      </StockIcon>
    </MultiAvatarWrapper>
  )
}

const BadgeIconWrapper = styled(Flex)<{ size: Size }>`
  width: 1.5rem;
  height: 1.5rem;
  padding: 0.125rem;
  position: absolute;
  border-radius: ${({ theme }) => theme.border.radius.circle};
  bottom: -0.25rem;
  right: -0.25rem;
  background-color: ${({ theme }) => theme.colors.backgroundSubtle};
  color: ${({ theme }) => theme.colors.iconStandard};
  border: ${({ theme }) => `0.125rem solid ${theme.colors.cardBackground};`};
  ${props => {
    switch (props.size) {
      case 'small':
        return css`
          bottom: -0.5rem;
          right: -0.5rem;
        `
      case 'large':
        return css`
          bottom: 0;
          right: 0;
        `
    }
    return
  }}
`

const BadgedAvatar = ({
  avatarData,
  avatarType,
  overlay,
  size = 'medium',
  alt = 'avatar image',
  disabled = false,
}: {
  avatarData: AvatarData
  avatarType: AvatarIconType
  overlay: string
  size: Size
  alt?: string
  disabled?: boolean
}) => {
  const badgedIcon = badgeIconMapping(overlay)
  return (
    <MultiAvatarWrapper disabled={disabled} size={size}>
      <BaseAvatar avatarData={avatarData} avatarType={avatarType} size={size} alt={alt} />
      <BadgeIconWrapper size={size} data-testid={overlay}>
        {badgedIcon}
      </BadgeIconWrapper>
    </MultiAvatarWrapper>
  )
}

export const BaseAvatar = ({
  avatarData,
  avatarType,
  size = 'medium',
  alt = 'avatar image',
  disabled = false,
  customerData,
}: {
  avatarData: AvatarData
  avatarType: AvatarIconType
  size: Size
  alt?: string
  disabled?: boolean
  customerData?: CustomerData
}) => {
  const theme = useTheme()
  const backgroundColor = getThemedAvatarColor({ theme, avatarData, customerData })
  const borderColor = theme.darkMode ? avatarData.dark_theme_border_color : avatarData.border_color
  const icon = avatarData?.icon?.arcade_id ? iconIdMapping[avatarData.icon.arcade_id] : undefined

  if (avatarType === 'Icon') {
    if (!avatarData?.icon?.arcade_id) {
      // What to do if this error is seen:
      // - Ask server team if missing icon id is intentional
      // - If it _was_ intentional then this logger should probably be removed since an icon avatar without an
      //   icon would be considered a valid state.
      throw Error('Avatar: Icon type missing icon prop')
    } else if (!icon) {
      // What to do if this error is seen:
      // - Check the iconIdMapping in the icons package to see if the icon is missing. If missing,
      //   confirm that it's a valid id and then add it to the mapping.
      throw Error(`Avatar: No icon mapping found for id ${avatarData.icon.arcade_id}`)
    }
  }

  return (
    <AvatarWrapper
      background={backgroundColor}
      borderColor={borderColor}
      color={avatarType === 'Icon' ? theme.arcadeColors.semantic.textInverse : undefined}
      size={size}
      justifyContent="center"
      alignItems="center"
      disabled={disabled}
      aria-label={alt}
    >
      {avatarType === 'Monogram' && <MonogramAvatar avatarData={avatarData} />}
      {(avatarType === 'Logo' || avatarType === 'UserImage') && (
        <UrlImageAvatar avatarData={avatarData} size={size} theme={theme} alt={alt} />
      )}
      {avatarType === 'Icon' && icon && <icon.Icon size={icon.size} />}
    </AvatarWrapper>
  )
}

const translateAvatarPropstoArcadeAvatarProps = (
  avatarData: AvatarData,
  size?: Size,
  alt?: string,
  disabled?: boolean,
  overlay?: string,
  overlayShape?: string,
  customerData?: CustomerData,
  avatarType?: AvatarIconType
) => {
  // Common props shared between both avatars
  const arcadeAvatarProps: ArcadeAvatarProps = {
    size,
    alt,
    disabled,
  }

  const {
    image_url,
    dark_theme_image_url,
    accent_color,
    dark_theme_accent_color,
    border_color,
    dark_theme_border_color,
    icon,
    initial,
    colorize_image,
  } = avatarData

  if ((avatarType === 'UserImage' || avatarType === 'Logo') && image_url) {
    arcadeAvatarProps.image = {
      lightUrl: image_url,
      darkUrl: dark_theme_image_url,
    }
  }

  if (avatarType === 'Icon' && icon?.arcade_id) {
    arcadeAvatarProps.icon = {
      arcadeId: icon.arcade_id,
    }
  }

  if (avatarType === 'Monogram' && initial) {
    arcadeAvatarProps.initial = initial
  }

  if (accent_color) {
    arcadeAvatarProps.backgroundColor = {
      light: accent_color,
      dark: dark_theme_accent_color ?? accent_color,
    }
  } else {
    const arcadeBackgroundColor = arcadeCustomerColorWithTheme({
      threadedCustomerToken: customerData?.threadedCustomerToken,
      customerToken: customerData?.customerToken,
      fullName: customerData?.fullName,
      smsNumber: customerData?.smsNumber,
      emailAddress: customerData?.emailAddress,
    })
    arcadeAvatarProps.backgroundColor = arcadeBackgroundColor
  }

  if (border_color) {
    arcadeAvatarProps.borderColor = {
      light: border_color,
      dark: dark_theme_border_color ?? border_color,
    }
  }

  if (colorize_image) {
    arcadeAvatarProps['tintColor'] = arcadeColors.textInverse
  }

  if (overlay && overlayShape === 'CIRCULAR_BADGE_SHAPE') {
    const overlayIcon = badgeIconMapping(overlay)
    if (overlayIcon) {
      arcadeAvatarProps.overlayIcon = {
        element: overlayIcon,
      }
    } else {
      arcadeAvatarProps.overlayImage = {
        lightUrl: overlay,
      }
    }
  }

  if (overlay && (overlayShape === 'CARD_SHAPE' || overlayShape === 'CIRCULAR_SHAPE')) {
    const stackedAvatars = []
    stackedAvatars.push({
      image: {
        lightUrl: overlay,
      },
      ...(overlayShape === 'CARD_SHAPE' && { scaleFactor: DEFAULT_CARD_SCALE_FACTOR }),
    })

    stackedAvatars.push(arcadeAvatarProps)

    // return stacked avatar
    return {
      stackedAvatars,
      disabled,
    }
  }

  return arcadeAvatarProps
}

/**
 * @deprecated Use ArcadeAvatar instead
 */
export const Avatar = ({
  avatarData,
  avatarType,
  size = 'medium',
  alt = 'avatar image',
  disabled = false,
  overlay,
  overlayShape,
  customerData,
}: {
  avatarData: AvatarData
  avatarType: AvatarIconType
  size: Size
  alt?: string
  disabled?: boolean
  overlay?: string
  overlayShape?: string
  customerData?: CustomerData
}) => {
  const shouldUseArcadeAvatar = useArcadeMigrationFeatureFlag('avatar')
  if (shouldUseArcadeAvatar) {
    const arcadeAvatarProps = translateAvatarPropstoArcadeAvatarProps(
      avatarData,
      size,
      alt,
      disabled,
      overlay,
      overlayShape,
      customerData,
      avatarType
    )
    return <ArcadeAvatar {...arcadeAvatarProps} />
  }

  if (overlay && overlayShape === 'CIRCULAR_BADGE_SHAPE') {
    return (
      <BadgedAvatar avatarData={avatarData} avatarType={avatarType} disabled={disabled} size={size} overlay={overlay} />
    )
  }

  if (overlay && (overlayShape === 'CARD_SHAPE' || overlayShape === 'CIRCULAR_SHAPE')) {
    return (
      <StackedAvatar
        avatarData={avatarData}
        avatarType={avatarType}
        size={size}
        disabled={disabled}
        overlay={overlay}
        overlayShape={overlayShape}
      />
    )
  }

  return (
    <BaseAvatar
      avatarData={avatarData}
      avatarType={avatarType}
      size={size}
      disabled={disabled}
      alt={alt}
      customerData={customerData}
    />
  )
}
